// The grid itself (Leon, 2026-10-23, mockup v13): row numbers, a frozen
// header and first column, whole-row selection (a box of cells only when a
// drag crosses columns), the active cell, status pills, double-click for a
// cell's full value, resizable columns and a totals row pinned to the bottom. Only the lines in view are drawn, so a
// log of thousands of rows scrolls smoothly. It owns no data rules — those
// come in as props from SheetTable.

import { cn } from "@bednbody/ui/lib/utils";
import { Button } from "@bednbody/ui/components/button";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from "@phosphor-icons/react";
import {
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { SheetLine } from "./sheet-group";
import { inBox, type Modifiers, type RangeBox } from "./sheet-selection";
import type { SheetColumn, SheetRow, SortDir } from "./sheet-table.model";

export const ROW_HEIGHT = 42;
/** Rows in view at once (Leon, 2026-10-23: 15, so the summary bar stays on screen). */
export const VISIBLE_ROWS = 15;
const NUMBER_WIDTH = 48;
const OVERSCAN = 12;

export interface GridColumn extends SheetColumn {
  width: number;
  numeric: boolean;
  /** Monospace for refs, dates and money. */
  mono: boolean;
  /** A status shown as a coloured word (style F, Leon 2026-10-26): its tone, or null for plain text. */
  tone?: (text: string) => PillTone | null;
  /** A payment method shown with a small logo tile before its name. */
  logo?: (text: string) => LogoMark | null;
  /** How the cell reads on screen only, e.g. a shortened ID; copy, export and double-click keep the full text. */
  cell?: (text: string) => string;
  /** Booking refs: the repeated prefix quiet, the unique code bold. */
  emphasizeTail?: boolean;
}

const TONE_TEXT: Record<PillTone, string> = {
  success: "text-success",
  warning: "text-warning",
  muted: "text-muted-foreground",
  info: "text-info",
  primary: "text-primary",
  gold: "text-gold-foreground",
  destructive: "text-destructive",
};
/** "BNB-20261021-" + "KH3P" (or "KH3P-S2" for a split row). */
const REF_PARTS = /^(.*?-)([^-]+(?:-S\d+)?)$/;
const ZERO = /^₱?0(\.0+)?$/;

/** A logo tile: its short mark ("₱", "maya") and its token colours. */
export interface LogoMark {
  mark: string;
  className: string;
}

export type PillTone =
  | "success"
  | "warning"
  | "muted"
  | "info"
  | "primary"
  | "gold"
  | "destructive";

export interface SheetGridProps<T extends SheetRow> {
  columns: readonly GridColumn[];
  lines: readonly SheetLine<T>[];
  /** Text shown in a cell (labels, not stored codes). */
  display: (row: T, column: GridColumn) => string;
  box: RangeBox | null;
  isRowSelected: (index: number) => boolean;
  focus: { r: number; c: number } | null;
  sort: { key: string; dir: SortDir } | null;
  search: string;
  totals: Record<string, string>;
  totalLabel: string;
  /** Group headings read their money subtotals in these columns. */
  formatSum: (key: string, value: number) => string;
  headerMenu: (column: GridColumn) => ReactNode;
  empty: ReactNode;
  loading: boolean;
  onCellDown: (r: number, c: number, modifiers: Modifiers) => void;
  onCellDrag: (r: number, c: number) => void;
  onCellContext: (r: number, c: number) => void;
  onCellPeek: (r: number, c: number) => void;
  onSelectColumn: (c: number) => void;
  onSelectAll: () => void;
  onToggleGroup: (value: string) => void;
  onResize: (key: string, width: number) => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
}

function cellOf(target: EventTarget | null): { r: number; c: number } | null {
  const cell = (target as HTMLElement | null)?.closest?.("td[data-r]");
  if (!(cell instanceof HTMLElement)) return null;
  return { r: Number(cell.dataset.r), c: Number(cell.dataset.c) };
}

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const at = text.toLowerCase().indexOf(query);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-sm bg-gold/40 text-inherit">
        {text.slice(at, at + query.length)}
      </mark>
      {text.slice(at + query.length)}
    </>
  );
}

export function SheetGrid<T extends SheetRow>({
  columns,
  lines,
  display,
  box,
  isRowSelected,
  focus,
  sort,
  search,
  totals,
  totalLabel,
  formatSum,
  headerMenu,
  empty,
  loading,
  onCellDown,
  onCellDrag,
  onCellContext,
  onCellPeek,
  onSelectColumn,
  onSelectAll,
  onToggleGroup,
  onResize,
  onKeyDown,
}: SheetGridProps<T>) {
  const scroller = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(600);
  // Never taller than the window, so the totals row is always on screen
  // (Leon, 2026-10-26).
  const tallest = (VISIBLE_ROWS + 2) * ROW_HEIGHT + 8;
  const [maxHeight, setMaxHeight] = useState(tallest);
  useLayoutEffect(() => {
    const fit = () => {
      const top = scroller.current?.getBoundingClientRect().top ?? 0;
      // Room under the grid for the Shortcuts line and the page's padding.
      setMaxHeight(Math.max(320, Math.min(tallest, window.innerHeight - Math.max(top, 0) - 64)));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [tallest]);
  const [resizing, setResizing] = useState<{
    key: string;
    x: number;
    start: number;
    width: number;
  } | null>(null);
  const query = search.trim().toLowerCase();
  // Columns hidden to the left or right — shown as a fade and a button. The
  // browser's horizontal scrollbar is hidden: it sat on the totals row and cut
  // its numbers in half, and a second bar of our own read as redundant
  // (Leon, 2026-10-26). Sideways: these buttons, Shift+wheel or a trackpad.
  const [edges, setEdges] = useState({ left: false, right: false });
  function measureEdges(element: HTMLDivElement) {
    const left = element.scrollLeft > 2;
    const right = element.scrollLeft + element.clientWidth < element.scrollWidth - 2;
    setEdges((current) =>
      current.left === left && current.right === right ? current : { left, right },
    );
  }
  function scrollColumns(direction: 1 | -1) {
    const element = scroller.current;
    if (!element) return;
    element.scrollBy({
      left: direction * Math.max(240, element.clientWidth * 0.6),
      behavior: "smooth",
    });
  }

  useLayoutEffect(() => {
    const element = scroller.current;
    if (!element) return;
    measureEdges(element);
    const observer = new ResizeObserver(() => {
      setViewport(element.clientHeight || 600);
      measureEdges(element);
    });
    observer.observe(element);
    return () => observer.disconnect();
    // biome-ignore lint/correctness/useExhaustiveDependencies: measured again on every scroll
  }, []);
  // A column added, hidden or resized changes what is off screen.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the columns change
  useEffect(() => {
    if (scroller.current) measureEdges(scroller.current);
  }, [columns]);

  // A drag ends wherever the button is released, inside the grid or not.
  useEffect(() => {
    const stop = () => {
      dragging.current = false;
    };
    window.addEventListener("mouseup", stop);
    return () => window.removeEventListener("mouseup", stop);
  }, []);

  // Keep the active cell in view when the keyboard moves it off screen.
  const focusLine = focus
    ? lines.findIndex((line) => line.kind === "row" && line.index === focus.r)
    : -1;
  useEffect(() => {
    const element = scroller.current;
    if (!element || focusLine < 0) return;
    const top = focusLine * ROW_HEIGHT;
    const header = ROW_HEIGHT + 4;
    if (top < element.scrollTop) element.scrollTop = top;
    else if (top + ROW_HEIGHT * 2 + header > element.scrollTop + element.clientHeight) {
      element.scrollTop = top + ROW_HEIGHT * 2 + header - element.clientHeight;
    }
  }, [focusLine]);

  const first = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const last = Math.min(lines.length, Math.ceil((scrollTop + viewport) / ROW_HEIGHT) + OVERSCAN);
  const visible = lines.slice(first, last);
  const widthOf = (column: GridColumn) =>
    resizing?.key === column.key ? resizing.width : column.width;
  const span = columns.length + 1;

  function startResize(event: PointerEvent<HTMLDivElement>, column: GridColumn) {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    setResizing({ key: column.key, x: event.clientX, start: column.width, width: column.width });
  }

  const frozen = NUMBER_WIDTH + (columns[0] ? widthOf(columns[0]) : 0);

  return (
    <div className="relative min-w-0">
      <div
        aria-label="Log grid — click a row to select it, drag down for more, Ctrl+click to add one, Ctrl+C to copy"
        aria-multiselectable="true"
        className={cn(
          "relative min-h-64 overflow-auto rounded-xl bg-card shadow-card-quiet ring-1 ring-border outline-none focus-visible:ring-2 focus-visible:ring-ring",
          // No horizontal bar (see the edge buttons); a slim vertical one.
          "[&::-webkit-scrollbar]:h-0 [&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-corner]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-primary/35 hover:[&::-webkit-scrollbar-thumb]:bg-primary/60 [&::-webkit-scrollbar-track]:bg-transparent",
        )}
        onKeyDown={onKeyDown}
        onScroll={(event) => {
          setScrollTop(event.currentTarget.scrollTop);
          measureEdges(event.currentTarget);
        }}
        ref={scroller}
        role="grid"
        // Header and totals row are a row each, plus their borders.
        style={{ maxHeight }}
        tabIndex={0}
      >
        <table
          className="min-w-full table-fixed border-separate border-spacing-0 text-sm select-none"
          // An exact width, so the column widths hold and long text is cut off
          // with an ellipsis instead of stretching its column.
          style={{
            width: NUMBER_WIDTH + columns.reduce((total, column) => total + widthOf(column), 0),
          }}
        >
          <colgroup>
            <col style={{ width: NUMBER_WIDTH }} />
            {columns.map((column) => (
              <col key={column.key} style={{ width: widthOf(column) }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th
                className="sticky top-0 left-0 z-30 h-10 border-b border-border bg-card"
                scope="col"
              >
                <button
                  aria-label="Select all rows"
                  className="size-full text-xs text-muted-foreground hover:bg-muted-foreground/10 hover:text-foreground"
                  onClick={onSelectAll}
                  type="button"
                >
                  All
                </button>
              </th>
              {columns.map((column, c) => {
                const selected = box !== null && c >= box.c0 && c <= box.c1;
                return (
                  <th
                    aria-sort={
                      sort?.key === column.key
                        ? sort.dir === "asc"
                          ? "ascending"
                          : "descending"
                        : undefined
                    }
                    className={cn(
                      "group/head sticky top-0 z-20 h-10 border-b border-border p-0 text-xs font-semibold",
                      selected ? "bg-primary/10 text-primary" : "bg-card text-muted-foreground",
                      c === 0 ? "z-25" : null,
                    )}
                    key={column.key}
                    scope="col"
                    style={c === 0 ? { left: NUMBER_WIDTH } : undefined}
                  >
                    <div className="relative flex h-10 items-center gap-1 pr-1.5 pl-3.5">
                      <button
                        className={cn(
                          "min-w-0 flex-1 truncate text-left hover:text-foreground focus-visible:outline-none",
                          column.numeric ? "text-right" : null,
                        )}
                        onClick={() => onSelectColumn(c)}
                        title={`Select the ${column.label} column`}
                        type="button"
                      >
                        {column.label}
                        {sort?.key === column.key ? (
                          sort.dir === "asc" ? (
                            <ArrowUpIcon className="ml-1 inline size-3" />
                          ) : (
                            <ArrowDownIcon className="ml-1 inline size-3" />
                          )
                        ) : null}
                      </button>
                      {headerMenu(column)}
                      <div
                        aria-hidden="true"
                        className="absolute top-0 -right-1 z-10 h-full w-2 cursor-col-resize hover:bg-primary/40"
                        onPointerDown={(event) => startResize(event, column)}
                        onPointerMove={(event) => {
                          if (!resizing || resizing.key !== column.key) return;
                          setResizing({
                            ...resizing,
                            width: Math.max(64, resizing.start + event.clientX - resizing.x),
                          });
                        }}
                        onPointerUp={() => {
                          if (resizing) onResize(resizing.key, resizing.width);
                          setResizing(null);
                        }}
                      />
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody
            onContextMenu={(event) => {
              const cell = cellOf(event.target);
              if (cell) onCellContext(cell.r, cell.c);
            }}
            onDoubleClick={(event) => {
              const cell = cellOf(event.target);
              if (cell) onCellPeek(cell.r, cell.c);
            }}
            onMouseDown={(event: MouseEvent) => {
              if (event.button !== 0) return;
              const cell = cellOf(event.target);
              if (!cell) return;
              // A plain press may become a drag; Shift and Ctrl clicks never do.
              dragging.current = !event.shiftKey && !event.ctrlKey && !event.metaKey;
              onCellDown(cell.r, cell.c, {
                shift: event.shiftKey,
                ctrl: event.ctrlKey || event.metaKey,
              });
            }}
            onMouseOver={(event) => {
              if (!dragging.current) return;
              const cell = cellOf(event.target);
              if (cell) onCellDrag(cell.r, cell.c);
            }}
          >
            {loading ? (
              <tr>
                <td className="h-40 text-center text-muted-foreground" colSpan={span}>
                  Loading the log…
                </td>
              </tr>
            ) : lines.length === 0 ? (
              <tr>
                <td colSpan={span}>{empty}</td>
              </tr>
            ) : (
              <>
                {first > 0 ? (
                  <tr aria-hidden="true" style={{ height: first * ROW_HEIGHT }}>
                    <td colSpan={span} />
                  </tr>
                ) : null}
                {visible.map((line) =>
                  line.kind === "group" ? (
                    <tr key={`g:${line.value}`} style={{ height: ROW_HEIGHT }}>
                      <td
                        className="sticky left-0 z-10 border-b border-border bg-muted p-0"
                        colSpan={2}
                      >
                        <button
                          aria-expanded={!line.collapsed}
                          className="flex h-full w-full items-center gap-2 px-3 text-left text-sm hover:bg-muted-foreground/10"
                          onClick={() => onToggleGroup(line.value)}
                          type="button"
                        >
                          {line.collapsed ? (
                            <CaretRightIcon className="size-3.5" />
                          ) : (
                            <CaretDownIcon className="size-3.5" />
                          )}
                          <span className="truncate font-semibold text-foreground">
                            {line.label}
                          </span>
                          <span className="shrink-0 text-muted-foreground">{line.count}</span>
                        </button>
                      </td>
                      {columns.slice(1).map((column) => (
                        <td
                          className={cn(
                            "border-b border-border bg-muted px-2.5 font-mono text-xs font-semibold tabular-nums",
                            column.numeric ? "text-right" : null,
                          )}
                          key={column.key}
                        >
                          {column.key in line.sums
                            ? formatSum(column.key, line.sums[column.key] ?? 0)
                            : null}
                        </td>
                      ))}
                    </tr>
                  ) : (
                    <tr className="group" key={`r:${line.index}`} style={{ height: ROW_HEIGHT }}>
                      <th
                        className={cn(
                          "sticky left-0 z-10 cursor-pointer border-b border-border/50 text-center text-xs font-medium tabular-nums transition-colors",
                          isRowSelected(line.index)
                            ? "bg-primary text-primary-foreground"
                            : box !== null && line.index >= box.r0 && line.index <= box.r1
                              ? "bg-primary/15 text-primary"
                              : "bg-card text-muted-foreground/70 group-hover:bg-muted/50 group-hover:text-foreground",
                        )}
                        onMouseDown={(event: MouseEvent) => {
                          if (event.button !== 0) return;
                          dragging.current = !event.shiftKey && !event.ctrlKey && !event.metaKey;
                          onCellDown(line.index, 0, {
                            shift: event.shiftKey,
                            ctrl: event.ctrlKey || event.metaKey,
                          });
                        }}
                        scope="row"
                      >
                        {line.index + 1}
                      </th>
                      {columns.map((column, c) => {
                        const text = display(line.row, column);
                        const selected = isRowSelected(line.index) || inBox(box, line.index, c);
                        const active = focus?.r === line.index && focus.c === c;
                        const tone = text ? (column.tone?.(text) ?? null) : null;
                        const logo = text ? (column.logo?.(text) ?? null) : null;
                        const shown = text && column.cell ? column.cell(text) : text;
                        const ref = column.emphasizeTail && !query ? REF_PARTS.exec(text) : null;
                        const zero = column.numeric && ZERO.test(text);
                        return (
                          <td
                            aria-selected={selected}
                            className={cn(
                              "cursor-default truncate border-b border-border/50 px-3.5 transition-colors",
                              column.numeric ? "text-right" : null,
                              column.mono ? "font-mono tabular-nums" : null,
                              zero ? "text-muted-foreground/45" : null,
                              selected ? "bg-primary/10" : "bg-card group-hover:bg-muted/50",
                              active
                                ? "relative shadow-[inset_0_0_0_2px_var(--color-primary)]"
                                : null,
                              c === 0 ? "sticky z-5" : null,
                            )}
                            data-c={c}
                            data-r={line.index}
                            key={column.key}
                            style={c === 0 ? { left: NUMBER_WIDTH } : undefined}
                            title={shown !== text || text.length > 24 ? text : undefined}
                          >
                            {text === "" ? (
                              <span className="text-muted-foreground/50">—</span>
                            ) : tone ? (
                              <span className={cn("font-medium", TONE_TEXT[tone])}>
                                <Highlight query={query} text={text} />
                              </span>
                            ) : logo ? (
                              <span className="inline-flex max-w-full items-center gap-2">
                                <span
                                  aria-hidden="true"
                                  className={cn(
                                    "inline-flex h-4.5 min-w-6 shrink-0 items-center justify-center rounded-sm px-1 text-xs leading-none font-bold tracking-tight",
                                    logo.className,
                                  )}
                                >
                                  {logo.mark}
                                </span>
                                <span className="truncate">
                                  <Highlight query={query} text={text} />
                                </span>
                              </span>
                            ) : ref ? (
                              <>
                                <span className="text-muted-foreground/70">{ref[1]}</span>
                                <span className="font-semibold text-foreground">{ref[2]}</span>
                              </>
                            ) : (
                              <Highlight query={query} text={shown} />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ),
                )}
                {last < lines.length ? (
                  <tr aria-hidden="true" style={{ height: (lines.length - last) * ROW_HEIGHT }}>
                    <td colSpan={span} />
                  </tr>
                ) : null}
              </>
            )}
          </tbody>
          <tfoot>
            <tr>
              <th
                className="sticky bottom-0 left-0 z-30 h-9 border-t-2 border-r border-border bg-muted text-xs text-muted-foreground"
                scope="row"
              >
                Σ
              </th>
              {columns.map((column, c) => (
                <td
                  className={cn(
                    "sticky bottom-0 z-20 truncate border-t-2 border-r border-border bg-muted px-2.5 text-xs font-semibold",
                    column.numeric ? "text-right font-mono tabular-nums" : "text-muted-foreground",
                    c === 0 ? "z-25" : null,
                  )}
                  key={column.key}
                  style={c === 0 ? { left: NUMBER_WIDTH } : undefined}
                >
                  {c === 0 && !column.numeric ? totalLabel : (totals[column.key] ?? "")}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      {edges.left ? (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-px z-30 w-10 bg-linear-to-r from-foreground/8 to-transparent"
            style={{ left: frozen }}
          />
          <Button
            aria-label="Scroll to earlier columns"
            className="absolute top-[calc(50%-0.5rem)] z-40 -translate-y-1/2 rounded-full shadow-float"
            onClick={() => scrollColumns(-1)}
            size="icon-sm"
            style={{ left: frozen + 8 }}
            type="button"
            variant="outline"
          >
            <CaretLeftIcon />
          </Button>
        </>
      ) : null}
      {edges.right ? (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-px right-px z-30 w-12 rounded-r-xl bg-linear-to-l from-foreground/8 to-transparent"
          />
          <Button
            aria-label="Scroll to more columns"
            className="absolute top-[calc(50%-0.5rem)] right-3 z-40 -translate-y-1/2 rounded-full shadow-float"
            onClick={() => scrollColumns(1)}
            size="icon-sm"
            type="button"
            variant="outline"
          >
            <CaretRightIcon />
          </Button>
        </>
      ) : null}
    </div>
  );
}
